<template>
  <div class="relation-upload-container">
    <h3 class="title">Relacionar Videos y Capítulos</h3>

    <div class="upload-section">
      <label class="file-label">
        <span>Seleccionar archivo Excel (.xlsx, .xls)</span>
        <input type="file" @change="handleFileUpload" accept=".xlsx, .xls" class="file-input" />
      </label>
    </div>

    <div v-if="excelData.length" class="table-container">
      <table class="relation-table">
        <thead>
          <tr>
            <th class="checkbox-column">
              <input type="checkbox" @change="toggleAllSelection" :checked="allSelected" />
            </th>
            <th v-for="(header, index) in tableHeaders" :key="index" class="header-cell">
              {{ header }}
            </th>
            <th class="status-column">Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in excelData" :key="rowIndex" class="data-row">
            <td class="checkbox-column">
              <input type="checkbox" v-model="selectedRows" :value="row.data" />
            </td>
            <td v-for="(cell, cellIndex) in row.data" :key="cellIndex" class="data-cell">
              {{ cell }}
            </td>
            <td :class="getStatusClass(row.status)">
              {{ row.status || 'Pendiente' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selectedRows.length" class="actions-section">
      <button class="action-button" @click="guardarRelaciones">
        Guardar Relaciones
      </button>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import MasivoService from '@/services/MasivoService'; // Asegúrate de crear este servicio

export default {
  data() {
    return {
      excelData: [],
      tableHeaders: [],
      selectedRows: [],
      allSelected: false,
    };
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        this.tableHeaders = jsonData[0];
        this.excelData = jsonData.slice(1).map(row => ({
          data: row,
          status: ''
        }));
      };
      reader.readAsArrayBuffer(file);
    },

    toggleAllSelection(event) {
      this.selectedRows = event.target.checked 
        ? this.excelData.map(row => row.data) 
        : [];
      this.allSelected = event.target.checked;
    },

    async guardarRelaciones() {
      const errores = [];
      
      try {
        this.excelData = this.excelData.map(row => {
          if (this.selectedRows.includes(row.data)) {
            return { ...row, status: 'Procesando' };
          }
          return row;
        });

        await Promise.all(
          this.selectedRows.map(async (rowData) => {
            try {
              const payload = {
                id_video: rowData[0] || null,      // ID del video
                id_capitulo: rowData[1] || null,   // ID del capítulo
                orden: rowData[2] || 0,           // Orden dentro del capítulo (opcional)
                estado: rowData[3] || 'activo',   // Estado por defecto 'activo'
              };

              if (!payload.id_video || !payload.id_capitulo) {
                throw new Error("Faltan campos obligatorios (ID video o ID capítulo)");
              }

              await MasivoService.guardarRelacion(payload);
              this.updateRowStatus(rowData, 'Éxito');

            } catch (error) {
              const errorMsg = error.message || "Error al guardar la relación";
              errores.push({ ...rowData, error: errorMsg });
              this.updateRowStatus(rowData, `Error: ${errorMsg}`);
            }
          })
        );

        if (errores.length > 0) {
          this.generarExcelErrores(errores);
        } else {
          alert("Todas las relaciones fueron guardadas exitosamente.");
        }

        this.selectedRows = [];
        this.allSelected = false;

      } catch (error) {
        console.error("Error en el proceso:", error);
      }
    },

    updateRowStatus(rowData, status) {
      this.excelData = this.excelData.map(row => {
        if (row.data === rowData) {
          return { ...row, status };
        }
        return row;
      });
    },

    generarExcelErrores(errores) {
      const headers = [...this.tableHeaders, "Error"];
      const data = [headers, ...errores.map(row => [...row, row.error])];
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Errores");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Errores_Relaciones.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    },

    getStatusClass(status) {
      if (status === 'Éxito') return 'status-success';
      if (status && status.startsWith('Error')) return 'status-error';
      if (status === 'Procesando') return 'status-processing';
      return 'status-pending';
    }
  }
};
</script>

<style scoped>
.relation-upload-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Arial', sans-serif;
}

.title {
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.upload-section {
  margin-bottom: 30px;
  text-align: center;
}

.file-label {
  display: inline-block;
  padding: 12px 20px;
  background-color: #f5f5f5;
  border: 2px dashed #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-label:hover {
  background-color: #e9ecef;
  border-color: #007bff;
}

.file-input {
  display: none;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow-x: auto;
}

.relation-table {
  width: 100%;
  border-collapse: collapse;
}

.header-cell, .data-cell {
  padding: 12px 15px;
}

.checkbox-column {
  width: 50px;
  text-align: center;
}

.status-column {
  width: 150px;
}

.header-cell {
  background-color: #007bff;
  color: white;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 14px;
}

.data-row:nth-child(even) {
  background-color: #f9f9f9;
}

.data-row:hover {
  background-color: #f1f3f5;
}

.status-success {
  color: #28a745;
  font-weight: 600;
}

.status-error {
  color: #dc3545;
  font-weight: 600;
}

.status-processing {
  color: #ffc107;
  font-weight: 600;
}

.status-pending {
  color: #6c757d;
}

.actions-section {
  margin-top: 25px;
  text-align: center;
}

.action-button {
  padding: 12px 25px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.action-button:hover {
  background-color: #0056b3;
}

.action-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}
</style>